// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { type CreateStaffPatientInput, createStaffPatient } from '../../services/create-staff-patient';
import { showErrorNotification } from '../../utils/notifications';

interface AddPatientModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: (patient: Patient) => void;
}

const EMPTY_FORM: CreateStaffPatientInput = { firstName: '', lastName: '', email: '', birthDate: '' };

export function AddPatientModal({ opened, onClose, onCreated }: AddPatientModalProps): JSX.Element {
  const medplum = useMedplum();
  const [form, setForm] = useState(EMPTY_FORM);
  const [matches, setMatches] = useState<Patient[]>([]);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = (): void => {
    setForm(EMPTY_FORM);
    setMatches([]);
    setAllowDuplicate(false);
    setLoading(false);
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const update = (field: keyof CreateStaffPatientInput, value: string): void => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setMatches([]);
    setAllowDuplicate(false);
  };

  const handleSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      const result = await createStaffPatient(medplum, form, allowDuplicate);
      if (result.status === 'duplicates') {
        setMatches(result.matches);
        return;
      }
      notifications.show({ color: 'green', message: 'Patient added and marked pending intake.' });
      onCreated(result.patient);
      reset();
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setLoading(false);
    }
  };

  const valid = Boolean(form.firstName.trim() && form.lastName.trim() && form.email.trim() && form.birthDate);

  return (
    <Modal opened={opened} onClose={close} title="Add patient" centered>
      <Stack>
        <Text size="sm" c="dimmed">
          Add basic information now. Patient can complete intake after setting up portal account.
        </Text>
        <TextInput
          label="First name"
          required
          value={form.firstName}
          onChange={(e) => update('firstName', e.currentTarget.value)}
        />
        <TextInput
          label="Last name"
          required
          value={form.lastName}
          onChange={(e) => update('lastName', e.currentTarget.value)}
        />
        <TextInput
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={(e) => update('email', e.currentTarget.value)}
        />
        <TextInput
          label="Date of birth"
          type="date"
          required
          value={form.birthDate}
          onChange={(e) => update('birthDate', e.currentTarget.value)}
        />
        {matches.length > 0 && (
          <Alert color="yellow" title="Possible existing patient">
            <Stack gap="xs">
              {matches.map((patient) => (
                <Text key={patient.id} size="sm">
                  {patient.name?.[0]?.given?.join(' ')} {patient.name?.[0]?.family} ·{' '}
                  {patient.birthDate ?? 'DOB unknown'}
                </Text>
              ))}
              <Button variant="light" onClick={() => setAllowDuplicate(true)}>
                Create anyway
              </Button>
            </Stack>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={close} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={() => handleSubmit().catch(console.error)} loading={loading} disabled={!valid}>
            Add patient
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
